import { getInventoryConfig } from "@/api/inventory/getConfig";
import { getNFTBalanceOptions } from "@/api/nfts/getNFTBalance";
import {
	useTransferNFTBatchMutation,
	useTransferNFTMutation,
} from "@/api/nfts/transferNFT";
import {
	Button,
	type ButtonProps,
	Group,
	Modal,
	NumberInput,
	Select,
	Stack,
	Text,
} from "@mantine/core";
import { useForm, useStore } from "@tanstack/react-form";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import type { Address } from "viem";
import { getAddress } from "viem/utils";
import { z } from "zod";

const formSchema = z.object({
	fromAddress: z.string().min(1, "From address is required"),
	toAddress: z.string().min(1, "To address is required"),
	amount: z.number().min(1, "Amount must be at least 1").optional(),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	fromAddress: "",
	toAddress: "",
	amount: 1,
};

export function TransferButton({
	type,
	name,
	collection,
	tokenId,
	...buttonProps
}: {
	type: "ERC721A" | "ERC1155";
	name: string;
	collection: string;
	tokenId: number;
} & ButtonProps) {
	const transferNFTMutation = useTransferNFTMutation();
	const transferBatchMutation = useTransferNFTBatchMutation();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const config = useQuery(getInventoryConfig());

	const form = useForm({
		defaultValues: DEFAULT_VALUES,
		validators: {
			onChange: formSchema,
		},
		onSubmit: async ({ value }) => {
			if (type === "ERC721A") {
				await transferNFTMutation.mutateAsync({
					from: value.fromAddress,
					to: value.toAddress,
					collectionAddress: collection,
					tokenId: tokenId.toString(),
				});
			} else {
				await transferBatchMutation.mutateAsync({
					from: value.fromAddress,
					to: value.toAddress,
					collectionAddress: collection,
					tokenAmounts: [
						{
							tokenId: Number(tokenId),
							amount: value.amount ?? 1,
						},
					],
				});
			}
			setIsModalOpen(false);
		},
	});

	const handleClose = () => {
		setIsModalOpen(false);
		form.reset();
	};

	const isTransferPending =
		transferNFTMutation.isPending || transferBatchMutation.isPending;

	const canSubmit = useStore(form.store, (state) => state.canSubmit);

	if (!config.data) return null;

	const fromWalletOptions = [
		{
			value: config.data.kmsAddresses.inventory,
			label: "Inventory",
			disabled: true,
		},
		{
			value: getAddress(config.data.kmsAddresses.minter),
			label: "Vault",
		},
	].filter((option) => option.value);

	const toWalletOptions = [
		{
			value: config.data.kmsAddresses.inventory,
			label: `Inventory (${config.data.kmsAddresses.inventory})`,
		},
		{
			value: getAddress(config.data.kmsAddresses.minter),
			label: `Vault (${config.data.kmsAddresses.minter})`,
		},
		...(config.data?.marketplaceWallets ?? []).map((wallet) => ({
			value: wallet,
			label: `Marketplace (${wallet})`,
		})),
	].filter((option) => option.value);

	const fromWalletsBalances = useQueries({
		queries: fromWalletOptions.map((option) =>
			getNFTBalanceOptions(
				type,
				collection as Address,
				tokenId,
				option.value as Address,
			),
		),
	});

	const getFromWalletOptions = useCallback(() => {
		return fromWalletOptions.map((option, index) => ({
			...option,
			label: `${option.label} (balance: ${fromWalletsBalances[index].data ?? 0})`,
			disabled: !fromWalletsBalances[index].data,
		}));
	}, [fromWalletOptions, fromWalletsBalances]);

	return (
		<>
			<Button
				{...buttonProps}
				onClick={() => setIsModalOpen(true)}
				loading={isTransferPending}
			>
				Transfer
			</Button>

			<Modal
				opened={isModalOpen}
				onClose={handleClose}
				title="Transfer NFT"
				size="xl"
				closeButtonProps={{
					display: "none",
				}}
				styles={{
					content: {
						borderRadius: "8px",
						position: "relative",
					},
					title: {
						fontSize: "24px",
						fontWeight: 700,
						lineHeight: 1.2,
					},
				}}
			>
				<Text size="lg" fw={500} mb="md">
					{name}
				</Text>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						void form.handleSubmit();
					}}
				>
					<Stack gap="md">
						<form.Field name="fromAddress">
							{(field) => (
								<Select
									label="From Address"
									placeholder="Select from address"
									data={getFromWalletOptions()}
									value={field.state.value}
									onChange={(value) => field.handleChange(value ?? "")}
									error={!!field.state.meta.errors[0]}
									required
								/>
							)}
						</form.Field>

						<form.Field name="toAddress">
							{(field) => (
								<Select
									label="To Address"
									placeholder="Select to address"
									data={toWalletOptions}
									value={field.state.value}
									onChange={(value) => field.handleChange(value ?? "")}
									error={!!field.state.meta.errors[0]}
									required
								/>
							)}
						</form.Field>

						{type === "ERC1155" && (
							<form.Field name="amount">
								{(field) => (
									<NumberInput
										label="Amount"
										placeholder="Enter amount"
										value={field.state.value}
										onChange={(value) => field.handleChange(Number(value) || 1)}
										error={!!field.state.meta.errors[0]}
										min={1}
										required
									/>
								)}
							</form.Field>
						)}

						<Group justify="flex-end" mt="md">
							<Button variant="light" onClick={handleClose}>
								Cancel
							</Button>
							<Button
								type="submit"
								loading={isTransferPending}
								disabled={!canSubmit}
							>
								Transfer
							</Button>
						</Group>
					</Stack>
				</form>
			</Modal>
		</>
	);
}
